import {useRef, useLayoutEffect} from 'react'
import utils from '@eitje/utils'
import {useId} from '@floating-ui/react'
import {EitjeVirtuoso} from '../../../eitje_virtuoso'
import {Layout} from '../../../layout'
import {useSelectContext, useFloatingSelectContext} from '../../context'
import {useFilteredItems, useSelectItemsContainerStyle} from './hooks'
import {SelectEmptyList} from '../select_empty_list'
import {SelectGroup} from '../select_group'
import {SelectItem} from '../select_item'
import {SelectNestedGroup} from '../select_nested_group'

export const SelectItems = ({emptyPlaceholderProps}) => {
	const id = useId()
	const {items, expandedGroups, virtualized} = useSelectContext()
	const filteredItems = useFilteredItems(items, expandedGroups)
	const {containerRef, style} = useSelectItemsContainerStyle()

	const customScrollParent = `scroll-container-${id}`
	const classNames = utils.makeCns('select-items', virtualized && 'virtualized', virtualized && customScrollParent)

	return (
		<Layout ref={containerRef} className={classNames} block baseColor={null} {...style}>
			{virtualized && <VirtualizedSelectItems data={filteredItems} customScrollParent={`.${CSS.escape(customScrollParent)}`} />}
			{!virtualized && filteredItems.map(item => <SelectItemDecider key={item.originalIndex} item={item} />)}
			<SelectEmptyList emptyPlaceholderProps={emptyPlaceholderProps} />
		</Layout>
	)
}

const VirtualizedSelectItems = ({data, customScrollParent}) => {
	const virtuosoRef = useRef()
	const {selectedIndex} = useSelectContext()
	const {activeIndex} = useFloatingSelectContext()

	const scrollToIndex = activeIndex != null ? activeIndex : (selectedIndex ?? 0)
	const initialTopMostItemIndex = scrollToIndex != null ? {index: scrollToIndex, align: 'center'} : 0

	// On filter/search changes (data changes) or target index changes, actively scroll
	useLayoutEffect(() => {
		if (!virtuosoRef.current || !data?.length) return

		const clamped = Math.max(0, Math.min(scrollToIndex, data.length - 1))

		// rAF gives Virtuoso a tick to reconcile the new items/range after filtering
		const id = requestAnimationFrame(() => {
			virtuosoRef.current?.scrollToIndex({index: clamped, align: 'center'})
		})
		return () => cancelAnimationFrame(id)
	}, [scrollToIndex, data?.length])

	return (
		<EitjeVirtuoso
			ref={virtuosoRef}
			data={data}
			itemContent={(_, item) => <SelectItemDecider key={item.originalIndex} item={item} />}
			customScrollParent={customScrollParent}
			initialTopMostItemIndex={initialTopMostItemIndex}
		/>
	)
}

const SelectItemDecider = ({item}) => {
	if (item.type === 'group') return <SelectGroup item={item} />

	if (item.type === 'item') return <SelectItem item={item} />

	if (item.type === 'nested-group') return <SelectNestedGroup item={item} />

	return null // nested-item items are not rendered in this list
}
